import {
  SECTION_AREAS,
  type EffectiveAccess,
  type PermissionAccess,
  type ScopeRef,
} from '@aroo/shared';
import { builtSections, sections, type Section, type SectionId } from './sections';

/**
 * What the signed-in person (or the person being viewed as) can reach, derived from the
 * engine's output (`GET /auth/session` → `access.permissions`, each with the scopes it applies
 * at). The shell only ever asks "which sections", "can approve" and "at what scope", so screens
 * never need to know a permission's spelling.
 */
export interface Access {
  /** `area.screen.action` codes, e.g. `sales.dashboard.view_site`. */
  permissions: readonly string[];
  /** The maximal scopes each permission applies at (company > division > region > site). */
  permissionScopes: ReadonlyMap<string, readonly ScopeRef[]>;
  sections: readonly SectionId[];
  canApprove: boolean;
}

/**
 * Permission-code prefixes that open each section (from the shared section ↔ area map). A
 * section shows when the person holds any permission under one of its areas; the API still
 * checks every request (nothing is hidden only in the UI).
 */
export const SECTION_PERMISSION_PREFIXES: Record<SectionId, readonly string[]> = {
  sales: SECTION_AREAS.sales.map((area) => `${area}.`),
  // Phase 3: branch managers live in Operations, so Today and Exceptions sit there too.
  operations: [...SECTION_AREAS.operations.map((area) => `${area}.`), 'attendance.scope.'],
  people: SECTION_AREAS.people.map((area) => `${area}.`),
  // Fleet admin reviews the dash-camera checks under Safety › Cameras (the API lets them in).
  safety: [...SECTION_AREAS.safety.map((area) => `${area}.`), 'fleet.admin.manage'],
  customers: SECTION_AREAS.customers.map((area) => `${area}.`),
  hr: SECTION_AREAS.hr.map((area) => `${area}.`),
  property: SECTION_AREAS.property.map((area) => `${area}.`),
  fleet: SECTION_AREAS.fleet.map((area) => `${area}.`),
};

export const APPROVE_PERMISSION = 'holidays.requests.approve';

export function sectionsFromPermissions(permissions: readonly string[]): SectionId[] {
  return sections
    .filter((s) =>
      SECTION_PERMISSION_PREFIXES[s.id].some((prefix) =>
        permissions.some((p) => p.startsWith(prefix)),
      ),
    )
    .map((s) => s.id);
}

/** From codes alone (tests, and anywhere scope does not matter). */
export function accessFromPermissions(permissions: readonly string[]): Access {
  return accessFromEffective({
    permissions: permissions.map((code) => ({
      code,
      scope: { kind: 'company', id: null, code: 'ALL', name: 'Whole company' },
      scopes: [],
      siteCodes: [],
      sources: [],
    })),
    scopes: [],
  });
}

export function accessFromEffective(effective: EffectiveAccess): Access {
  const permissions = effective.permissions.map((p: PermissionAccess) => p.code);
  return {
    permissions,
    permissionScopes: new Map(effective.permissions.map((p) => [p.code, p.scopes])),
    sections: sectionsFromPermissions(permissions),
    canApprove: permissions.includes(APPROVE_PERMISSION),
  };
}

export const NO_ACCESS: Access = {
  permissions: [],
  permissionScopes: new Map(),
  sections: [],
  canApprove: false,
};

export function canAccess(access: Access, id: SectionId): boolean {
  return access.sections.includes(id);
}

export function hasPermission(access: Access, code: string): boolean {
  return access.permissions.includes(code);
}

/**
 * The Approvals tab: holiday approvers, plus HR / Aroo Admin who decide HR-approval absence
 * requests (2b-M2). The API filters each list to what the person may actually decide.
 */
export function canSeeApprovals(access: Access): boolean {
  return access.canApprove || hasPermission(access, 'absence.admin.manage');
}

/** The scopes a permission applies at; empty when not held. */
export function scopesFor(access: Access, code: string): readonly ScopeRef[] {
  return access.permissionScopes.get(code) ?? [];
}

/**
 * Sections the nav should show, in canonical nav order (not the order they were granted). Only
 * built sections.
 */
export function visibleSections(access: Access): Section[] {
  return builtSections.filter((s) => canAccess(access, s.id));
}

/** Built sections the role can't see (unbuilt ones aren't counted as "hidden"). */
export function hiddenSectionCount(access: Access): number {
  return builtSections.length - visibleSections(access).length;
}

/** Footer note for the nav, e.g. "2 sections hidden. You see what your Branch Manager role needs." */
export function hiddenSectionsNote(access: Access, roleTitle: string): string | null {
  const hidden = hiddenSectionCount(access);
  if (hidden === 0) return null;
  return `${hidden} section${hidden > 1 ? 's' : ''} hidden. You see what your ${roleTitle} role needs.`;
}
